// Loads platform-level Help Mode overrides from the API and exposes
// them to the in-app Help Dialog. Single shared cache for the page;
// refetched after a successful save so the user sees their edit
// immediately.

const BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "") + "/api";

export interface HelpOverride {
  testId: string;
  ar: string;
  en: string;
  updatedAt?: string;
}

export interface HelpOverridesSnapshot {
  canEdit: boolean;
  byId: Record<string, HelpOverride>;
}

let snapshot: HelpOverridesSnapshot = { canEdit: false, byId: {} };
let loaded = false;
let inflight: Promise<HelpOverridesSnapshot> | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function getSnapshot(): HelpOverridesSnapshot {
  return snapshot;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function loadHelpOverrides(force = false): Promise<HelpOverridesSnapshot> {
  if (loaded && !force) return snapshot;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${BASE}/help-overrides`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { canEdit: boolean; items: HelpOverride[] };
      const byId: Record<string, HelpOverride> = {};
      for (const it of data.items || []) byId[it.testId] = it;
      snapshot = { canEdit: !!data.canEdit, byId };
      loaded = true;
      notify();
      return snapshot;
    } catch {
      // Stay with whatever we had; don't break the UI for a missing backend.
      return snapshot;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function getOverride(testId: string | null | undefined): HelpOverride | undefined {
  if (!testId) return undefined;
  return snapshot.byId[testId];
}

export async function saveOverride(
  testId: string,
  ar: string,
  en: string,
): Promise<HelpOverride> {
  const res = await fetch(`${BASE}/platform/help-overrides/${encodeURIComponent(testId)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ar, en }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j?.error || `HTTP ${res.status}`);
  }
  const row = await res.json() as HelpOverride;
  snapshot = {
    ...snapshot,
    byId: { ...snapshot.byId, [testId]: { testId, ar: row.ar, en: row.en, updatedAt: row.updatedAt } },
  };
  notify();
  return row;
}

export async function deleteOverride(testId: string): Promise<void> {
  const res = await fetch(`${BASE}/platform/help-overrides/${encodeURIComponent(testId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
  const next = { ...snapshot.byId };
  delete next[testId];
  snapshot = { ...snapshot, byId: next };
  notify();
}
